import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../services/firebase.js';

export const MAX_DAILY_GENERATIONS = 5;

export const checkAIUsageLimit = async (cvr) => {
    const companyRef = doc(db, 'companies', cvr);
    const companyDoc = await getDoc(companyRef);
    const aiUsage = companyDoc.data()?.aiUsage;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Convert Firestore timestamp to Date for proper comparison
    const usageDate = aiUsage?.date?.toDate();
    const shouldReset = !aiUsage || 
                       !usageDate || 
                       usageDate.getTime() < today.getTime();

    if (shouldReset) {
        // Reset counter for new day
        await updateDoc(companyRef, {
            aiUsage: {
                date: today,
                count: 0  // Changed from 1 to 0
            }
        });
        return true;
    }

    if (aiUsage.count >= MAX_DAILY_GENERATIONS) {
        throw new Error('Du har nået grænsen for AI-genereret indhold i dag. Prøv igen i morgen.');
    }

    // Increment counter before generating content
    await updateDoc(companyRef, {
        'aiUsage.count': increment(1)
    });
    
    return true;
};

export const generateSEOContent = async (data) => {
    try {
        if (!data?.companyName) {
            throw new Error('Company name is required');
        }

        // Different prompt based on if it's a page or initial website setup
        const prompt = data.pageType ? `
            Skriv professionelt indhold til en side på ${data.companyName}'s hjemmeside.
            
            Branche: ${data.industryDesc}
            Side type: ${data.pageType}
            Nøgleord: ${data.keywords?.join(', ')}

            Generer følgende:
            1. En overskrift der:
               - Er SEO-venlig og max 60 tegn
               - KUN har stort begyndelsesbogstav i første ord
               - Inkluderer relevante nøgleord naturligt

            2. En brødtekst der SKAL:
               - Have 3-4 korte og fokuserede afsnit
               - Inkludere de angivne nøgleord naturligt
               - Have \n\n mellem hvert afsnit
               - Være minimum 300 ord
               - Være professionel og letlæselig
               - Være relevant for siden ${data.pageType}

            Format dit svar som følgende JSON:
            {
                "title": "SEO-venlig overskrift til siden",
                "content": "Brødtekst med afsnit..."
            }
        ` : `
            Skriv professionelt indhold til en håndværker hjemmeside for firmaet "${data.companyName}".
            ${data.industryDesc ? `\nBranche: ${data.industryDesc}` : ''}
            ${data.companyDesc ? `\nVirksomhedstype: ${data.companyDesc}` : ''}
            ${data.city ? `\nBy: ${data.city}` : ''} 
            ${data.mainContent ? `\nBaseret på eksisterende indhold: ${data.mainContent}` : ''}

            Generer følgende:
            1. En overskrift der:
               - Er SEO-venlig og max 60 tegn
               - KUN har stort begyndelsesbogstav i første ord
               - Er relevant for en håndværker hjemmeside
               - Inkluderer byen hvis angivet

            2. En kort undertekst der:
               - Er max 120 tegn
               - Beskriver firmaets kerneværdier
               - Er indbydende og professionel
               - Nævner det geografiske område hvis angivet

            3. En brødtekst der SKAL:
               - Have 4-6 korte og fokuserede afsnit
               - Starte med en stærk introduktion der nævner det geografiske område
               - Have et afsnit om firmaets ekspertise
               - Have et afsnit om kvalitet og erfaring
               - Have et afsnit om services/ydelser
               - Slutte med en tydelig call-to-action
               - Have \n\n mellem hvert afsnit
               - Være minimum 300 ord i alt
               - Være professionel og letlæselig
               - Fokusere på lokal tilstedeværelse hvis by er angivet

            Format dit svar som følgende JSON:
            {
                "title": "En SEO-venlig overskrift",
                "subtitle": "En kort undertekst om kerneværdier",
                "content": "Første afsnit med introduktion...\\n\\nAndet afsnit om ekspertise...\\n\\nTredje afsnit om kvalitet...\\n\\nFjerde afsnit om services...\\n\\nAfsluttende afsnit med call-to-action..."
            }
        `;

        console.log("Prompt", prompt);

        const response = await fetch('https://api.openai.com/v1/chat/completions', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`
            },
            body: JSON.stringify({
                model: "gpt-3.5-turbo",
                messages: [{
                    role: "system",
                    content: "Du er en professionel dansk tekstforfatter der skriver SEO-optimeret indhold til håndværkervirksomheder."
                }, {
                    role: "user",
                    content: prompt
                }],
                temperature: 0.7,
                max_tokens: 2000
            })
        });

        if (!response.ok) {
            throw new Error(`OpenAI API error: ${response.status}`);
        }

        const result = await response.json();
        
        try {
            const content = JSON.parse(result.choices[0].message.content);
            // Ensure only first word is capitalized in title
            content.title = content.title.toLowerCase().replace(/^\w/, c => c.toUpperCase());
            return content;
        } catch (parseError) {
            console.error('Failed to parse AI response:', parseError);
            return {
                title: `Velkommen til ${data.companyName}`,
                content: 'Vi er eksperter i vores felt og leverer altid kvalitetsarbejde til tiden.'
            };
        }
    } catch (error) {
        console.error('AI generation error:', error);
        return {
            title: `Velkommen til ${data.companyName}`,
            subtitle: 'Professionel service og kvalitetsarbejde',
            content: 'Vi er eksperter i vores felt og leverer altid kvalitetsarbejde til tiden.'
        };
    }
};